import { Question } from "@app/models/question.model";
import { Box, Card, styled, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import QuestionDropdown from "../question-dropdown/question-dropdown.component";
import QuestionRadio from "../question-radio/question-radio.component";
import QuestionCheckbox from "../question-checkbox/question-checkbox.component";
import QuestionTextInput from "../question-text-input/question-text-input.component";
import QuestionTableRadio from "../question-table-radio/question-table-radio.component";
import QuestionCollapseExpandRadio from "../question-collapse-expand-radio/question-collapse-expand-radio.component";
import QuestionTextarea from "../question-textarea/question-textarea.component";
import { QuestionType } from "@app/constants/common.const";
import { useContextMenu } from "@app/hooks/useContextMenu";
import {
  addHighlight,
  applyHighlights,
  getHighlightMenuItems,
  removeHighlight,
  removeOldHighLight,
} from "@app/utils/highlight.utils";
import { Section } from "@app/models/section.model";
import { AnswerRequest } from "@app/types/answer.type";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "24px",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    gap: "16px",
  },
  overflow: "auto",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiTypography-h5": {
    fontSize: "1.1rem",
    fontWeight: 500,
  },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  gap: theme.spacing(3),
  flex: 1,
  overflow: "auto",
}));

const PassageSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px",
  overflowY: "auto",
  backgroundColor: "#fff",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
}));

const QuestionsSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px",
  overflowY: "auto",
  backgroundColor: "#fff",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#555",
  },
}));

const PassageText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: 1.6,
  userSelect: "text",
  "& span[style*='background-color: yellow']": {
    backgroundColor: "yellow",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const QuestionBlock = styled(Box)(({ theme }) => ({
  marginBottom: "24px",
  padding: "16px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&:last-child": {
    marginBottom: 0,
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "16px",
  },
}));

const QuestionContent = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  paddingRight: "24px",
  [theme.breakpoints.down("md")]: {
    borderRight: "none",
    borderBottom: "1px solid #e0e0e0",
    paddingRight: 0,
    paddingBottom: "16px",
  },
}));

const AnswerContent = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
  },
}));

const QuestionHeader = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  marginBottom: "12px",
  color: theme.palette.primary.main,
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  marginBottom: "8px",
  userSelect: "text",
  "& span[style*='background-color: yellow']": {
    backgroundColor: "yellow",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

interface ExamReadingProps {
  testId: number;
  questionsBySection: Section[];
  currentQuestion: number;
  onChangeAnswer: (value: string | string[] | Record<number, string>, questionId: number) => void;
  onQuestionChange: (questionNumber: number) => void;
  onSelectedAnswersChange: (newAnswers: Record<number, AnswerRequest>) => void;
}

function ExamReading({
  currentQuestion,
  questionsBySection,
  onChangeAnswer,
  onQuestionChange,
  onSelectedAnswersChange
}: ExamReadingProps) {
  const { showContextMenu } = useContextMenu();
  const [selectedAnswers, setSelectedAnswers] = useState<
      Record<number, AnswerRequest>
    >({});
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const answerRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const getQuestionNumber = (sectionIndex: number, groupIndex: number, questionIndex: number): number => {
    let questionNumber = 1;
    
    for (let i = 0; i < sectionIndex; i++) {
      const section = questionsBySection[i];

      if (!section.questionGroups) continue;
      
      for (let j = 0; j < section.questionGroups.length; j++) {
        const group = section.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }
    
    const currentSection = questionsBySection[sectionIndex];

    if (currentSection.questionGroups) {
      for (let j = 0; j < groupIndex; j++) {
        const group = currentSection.questionGroups[j];

        if (!group.questions) continue;

        questionNumber += group.questions.length;
      }
    }
    
    return questionNumber + questionIndex;
  };

  const getCurrentSectionAndGroup = () => {
    let totalQuestions = 0;

    for (let sectionIndex = 0; sectionIndex < questionsBySection.length; sectionIndex++) {
      const section = questionsBySection[sectionIndex];
      
      if (!section.questionGroups) continue;

      for (let groupIndex = 0; groupIndex < section.questionGroups.length; groupIndex++) {
        const group = section.questionGroups[groupIndex];
        
        if (!group.questions) continue;

        const questionsInGroup = group.questions.length;
        
        if (currentQuestion > totalQuestions && currentQuestion <= totalQuestions + questionsInGroup) {
          return {
            section,
            sectionIndex,
            group,
            groupStartIndex: totalQuestions,
            groupIndex,
            questionIndex: currentQuestion - totalQuestions - 1
          };
        }

        totalQuestions += questionsInGroup;
      }
    }

    return null;
  };

  const currentSectionInfo = getCurrentSectionAndGroup();

  if (!currentSectionInfo) return null;

  const { group } = currentSectionInfo;

  useEffect(() => {
    scrollToQuestion(currentQuestion);
  }, [currentQuestion]);

  useEffect(() => {
    const questionElements = document.querySelectorAll(".question-text, .passage-text");

    questionElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        const questionId = element
          .closest(".question-block")
          ?.getAttribute("data-question-id");

        if (questionId) {
          applyHighlights(element, currentSectionInfo.section.id, parseInt(questionId));
        }
      }
    });
  }, [currentQuestion]);

  const scrollToQuestion = (questionNumber: number) => {
    const questionRef = questionRefs.current[questionNumber];
    const answerRef = answerRefs.current[questionNumber];

    if (questionRef) {
      questionRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (answerRef) {
      answerRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAnswerChange = (
    questionNumber: number,
    value: string | string[] | Record<number, string> | any,
    question: any,
    questionType?: QuestionType
  ) => {
    if (questionType === QuestionType.TABLE_RADIO) {
      const { question, selectedOption } = value;
      
      onChangeAnswer(selectedOption, question.questionNumber);
      setSelectedAnswers((prev) => {
        const updatedAnswers = {
          ...prev,
          [question.questionNumber]: {
            questionId: question.id,
            data: {
              textValue: selectedOption
            }
          },
        };

        
        onSelectedAnswersChange(updatedAnswers);
        
        return updatedAnswers;
      });
    } else if (questionType === QuestionType.CHECKBOX) {
      const { questions, selectedOption } = value;

      questions.forEach((question: any, index: number) => {
        setTimeout(() => {
          onChangeAnswer(selectedOption, question.questionNumber);
        }, index * 200);
        setSelectedAnswers((prev) => {
          const updatedAnswers = {
            ...prev,
            [question.questionNumber]: {
              questionId: question.id,
              data: {
                textValue: selectedOption
              }
            },
          };
          
          onSelectedAnswersChange(updatedAnswers);
            
          return updatedAnswers;
        });
      });

    } else {
      onChangeAnswer(value, questionNumber);
      setSelectedAnswers((prev) => {
        const updatedAnswers = {
          ...prev,
          [questionNumber]: {
            questionId: question.id,
            data: {
              textValue: value
            }
          },
        };
        
        onSelectedAnswersChange(updatedAnswers);

        return updatedAnswers;
      });
    }

  };

  const handleClickAnswer = (questionNumber: number) => {
    onQuestionChange(questionNumber);
  };

  const handleHighlight = (sectionNumber: number, questionNumber: number, element: HTMLElement) => {
    removeOldHighLight(element);
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      const textNode = range.startContainer;
  
      if (textNode.nodeType === Node.TEXT_NODE) {
        addHighlight(
          sectionNumber,
          questionNumber,
          textNode.textContent || "",
          range.startOffset,
          range.endOffset
        );
        applyHighlights(textNode.parentElement as HTMLElement, sectionNumber, questionNumber);
      }
    }
  };

  const handleRemoveHighlight = (sectionNumber: number, questionNumber: number, element: HTMLElement) => {
    const highlightIndex = element.getAttribute("data-highlight");

    if (highlightIndex !== null) {
      removeHighlight(sectionNumber, questionNumber, parseInt(highlightIndex));
      applyHighlights(element.parentElement as HTMLElement, sectionNumber, questionNumber);
    }
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    sectionNumber: number,
    questionNumber: number,
  ) => {
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      const targetElement = event.target as HTMLElement;
      const isHighlighted = targetElement.closest("[data-highlight]");

      
      if (!range.collapsed) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => handleHighlight(sectionNumber, questionNumber, targetElement),
            isHighlighted
              ? () =>
                  handleRemoveHighlight(
                    sectionNumber,
                    questionNumber,
                    isHighlighted as HTMLElement
                  )
              : undefined
          )
        );
      } else if (isHighlighted) {
        showContextMenu(
          event,
          getHighlightMenuItems(
            () => {},
            () =>
              handleRemoveHighlight(sectionNumber, questionNumber, isHighlighted as HTMLElement)
          )
        );
      }
    }
  };

  const renderQuestionInput = (
    question: Question,
    index: number,
    value: string | string[] | Record<number, string> | any,
    onChange: (value: string | string[] | Record<number, string> | any) => void,
    onFocus: (questionNumber: number) => void
  ) => {
    const questionNumber = getQuestionNumber(
      currentSectionInfo.sectionIndex,
      currentSectionInfo.groupIndex,
      index
    );

    const tableRadioQuestions = group.questions.filter(
      (q) => q.group.type === QuestionType.TABLE_RADIO
    );
    const checkboxQuestions = group.questions.filter((q) => q.group.type === QuestionType.CHECKBOX);

    switch (question.group.type) {
      case QuestionType.DROPDOWN:
        return (
          <QuestionDropdown
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.RADIO:
        return (
          <QuestionRadio
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.TABLE_RADIO:
        if (tableRadioQuestions[0].id === question.id) {
          const options = tableRadioQuestions[0].data.answer || [];

          return (
            <QuestionTableRadio
              questions={tableRadioQuestions.map((question, index) => { 
                return {
                  ...question, 
                  questionNumber: getQuestionNumber(currentSectionInfo.sectionIndex, currentSectionInfo.groupIndex, index)
                };
              }) as Question[]}
              options={options}
              value={value as Record<string, string>}
              onChange={(selectedOption: string, question: any) => {
                onChange({
                  question,
                  selectedOption
                });
              }}
              onFocus={(questionNumber: number) => {
                onFocus(questionNumber);
              }}
            />
          );
        }

        return null;
      case QuestionType.COLLAPSE_EXPAND_RADIO:
        return (
          <QuestionCollapseExpandRadio
            questionNumber={questionNumber}
            question={question}
            options={question.data.answer || []}
            value={value as string}
            onChange={(questionIndex: number, selectedOption: string) => {
              onChange(selectedOption);
            }}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.CHECKBOX:
        if (checkboxQuestions[0].id === question.id) {
          const options = checkboxQuestions[0].data.answer || [];

          return (
            <QuestionCheckbox
              options={options}
              questions={checkboxQuestions.map((question, index) => {
                return {
                  ...question, 
                  questionNumber: getQuestionNumber(currentSectionInfo.sectionIndex, currentSectionInfo.groupIndex, index)
                };
              }) as Question[]}
              value={value as string[]}
              onChange={(questions: Question[], value: string[]) => {
                onChange({
                  questions,
                  selectedOption: value
                });
              }}
              onFocus={onFocus as (questionNumber: number) => void}
            />
          );
        }

        return null;
      case QuestionType.IMAGE_TEXT_INPUT:
        return (
          <QuestionTextInput
            questionNumber={questionNumber}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
      case QuestionType.TEXTAREA:
        return (
          <QuestionTextarea
            questionNumber={questionNumber}
            value={value as string}
            onChange={onChange as (value: string) => void}
          />
        );
      default:
        return (
          <QuestionRadio
            questionNumber={questionNumber}
            options={question.data.answer || []}
            value={value as string}
            onChange={onChange as (value: string) => void}
            onFocus={onFocus as (questionNumber: number) => void}
          />
        );
    }
  };

  return (
    <Container>
      <HeaderCard elevation={1}>
        <Typography variant="h5">{currentSectionInfo.section.title}</Typography>
      </HeaderCard>

      <ContentCard>
        <PassageSection>
          <PassageText style={{ whiteSpace: "pre-line" }} className="passage-text" onContextMenu={(e) => handleContextMenu(e, currentSectionInfo.section.id, 0)}>
            {currentSectionInfo.section.data.passage}
          </PassageText>
        </PassageSection>

        <QuestionsSection>
          <Typography style={{ whiteSpace: "pre-line" }} variant="h6" onContextMenu={(e) => handleContextMenu(e, currentSectionInfo.section.id, 0)}>{currentSectionInfo.group.title}</Typography>
          {group.data?.image && (
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={group.data.image}
                alt={"Group image"}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "4px",
                }}
              />
            </Box>
          )}
          {![QuestionType.CHECKBOX, QuestionType.TABLE_RADIO].includes(group.type) && group.questions.map((question, index) => {
            const questionNumber = getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              index
            );

            return (
              <QuestionBlock
                key={question.id}
                className="question-block"
                data-question-id={question.id}
                ref={(el: HTMLDivElement | null) => {
                  questionRefs.current[questionNumber] = el;
                }}
                onContextMenu={(e) => handleContextMenu(e, currentSectionInfo.section.id, questionNumber)}
              > 
                <QuestionContent>
                  <QuestionHeader>Question {questionNumber}</QuestionHeader>
                  <QuestionText className="question-text">
                    { [QuestionType.COLLAPSE_EXPAND_RADIO, QuestionType.TABLE_RADIO, QuestionType.CHECKBOX].includes(question.group.type) ? "" : question.text}
                  </QuestionText>
                </QuestionContent>
  
                <AnswerContent>
                  {renderQuestionInput(
                    question,
                    index,
                    selectedAnswers[questionNumber]?.data?.textValue ||
                        (question.group.type ===
                        QuestionType.CHECKBOX
                          ? []
                          : ""),
                    (value) => handleAnswerChange(questionNumber, value, question),
                    () => handleClickAnswer(questionNumber),
                  )}
                </AnswerContent>
              </QuestionBlock>
            );
          })}
          {group.type === QuestionType.TABLE_RADIO && group.questions.map((question, index) => {
            const questionNumber = getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              index
            );
            const lastQuestionNumber = getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              group.questions.length - 1
            );

            if (index === 0) {
              return (
                <QuestionBlock
                  key={question.id}
                  className="question-block"
                  data-question-id={question.id}
                  ref={(el: HTMLDivElement | null) => {
                    questionRefs.current[questionNumber] = el;
                  }}
                  onContextMenu={(e) => handleContextMenu(e, currentSectionInfo.section.id, questionNumber)}
                > 
                  <QuestionContent>
                    <QuestionHeader>Question {questionNumber} - {lastQuestionNumber}</QuestionHeader>
                    <QuestionText className="question-text"></QuestionText>
                  </QuestionContent>
    
                  <AnswerContent>
                    {renderQuestionInput(
                      question,
                      index,
                      selectedAnswers,
                      (value) => handleAnswerChange(questionNumber, value, question, question.group.type),
                      (questionNumber) => handleClickAnswer(questionNumber),
                    )}
                  </AnswerContent>
                </QuestionBlock>
              );
            }
          })}
          {group.type === QuestionType.CHECKBOX && group.questions.map((question, index) => {
            const questionNumber = getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              index
            );
            const lastQuestionNumber = getQuestionNumber(
              currentSectionInfo.sectionIndex,
              currentSectionInfo.groupIndex,
              group.questions.length - 1
            );

            if (index === 0) {
              return (
                <QuestionBlock
                  key={question.id}
                  className="question-block"
                  data-question-id={question.id}
                  ref={(el: HTMLDivElement | null) => {
                    questionRefs.current[questionNumber] = el;
                  }}
                  onContextMenu={(e) => handleContextMenu(e, currentSectionInfo.section.id, questionNumber)}
                > 
                  <QuestionContent>
                    <QuestionHeader>Question {questionNumber} - {lastQuestionNumber}</QuestionHeader>
                    <QuestionText className="question-text">
                      { [QuestionType.COLLAPSE_EXPAND_RADIO, QuestionType.TABLE_RADIO].includes(question.group.type) ? "" : question.text}
                    </QuestionText>
                  </QuestionContent>
    
                  <AnswerContent>
                    {renderQuestionInput(
                      question,
                      index,
                      selectedAnswers[questionNumber]?.data?.textValue,
                      (value) => handleAnswerChange(questionNumber, value, question, question.group.type),
                      (questionNumber) => handleClickAnswer(questionNumber),
                    )}
                  </AnswerContent>
                </QuestionBlock>
              );
            }
          })}
        </QuestionsSection>
      </ContentCard>
    </Container>
  );
}

export default ExamReading;
